import { Icon } from '@dynamic/ui';
import styled, { css } from 'styled-components';

import { ThemeConfig } from '#/service/config';
import { horizontalDirection } from '#/service/constants';
import { handleOpacity, remify } from '#/service/utils';

import { fixFocusInSafari } from '../../common';
import { StyledInputProps } from '../Form.types';
import { InputLabelProps, StyledIconProps } from './FormInput.types';

export const cssInputBase = (
  highlighted: boolean,
  hasIcon: boolean,
  theme: ThemeConfig
) => css`
  height: 100%;
  width: 100%;
  font: var(--fontStyleBody);
  box-sizing: border-box;
  padding: ${remify(17)} ${remify(hasIcon ? 44 : 12)} ${remify(3)};
  color: ${highlighted
    ? theme.color.inputForeground
    : handleOpacity(theme.color.inputForeground, 0.6)};
  background-color: var(--inputBackground);
`;

export const Container = styled.div`
  margin: ${remify(20)} 0;
  position: relative;

  &:first-of-type {
    margin-top: 0;
  }
`;

export const InputContainer = styled.div`
  position: relative;
  height: ${remify(50)};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const InputLabel = styled.label<InputLabelProps>`
  position: absolute;
  ${({ $bigLabel }) => !$bigLabel && 'top: 0;'}
  left: ${({ $hasIcon }) => remify($hasIcon ? 44 : 12)};
  right: ${remify(5)};
  padding: ${remify(4)} 0;
  font: ${({ $bigLabel }) =>
    $bigLabel ? `var(--fontStyleBody)` : `var(--fontStyleCaption1)`};
  color: ${({ $highlighted, theme }) =>
    $highlighted
      ? theme.color.inputForeground
      : handleOpacity(theme.color.inputForeground, 0.6)};
  cursor: text;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

export const StyledIcon = styled(Icon)<StyledIconProps>`
  position: absolute;
  top: ${remify(13)};

  ${({ position, $highlighted, theme }) => {
    const isRight = position === horizontalDirection.RIGHT;

    return `
      color: ${
        $highlighted && isRight
          ? theme.color.inputForeground
          : handleOpacity(
              theme.color.inputForeground,
              $highlighted || isRight ? 0.6 : 0.4
            )
      };
      ${position}: ${remify(12)};
      ${isRight ? 'cursor: pointer;' : ''}`;
  }}
`;

export const StyledInput = styled('input')<StyledInputProps>`
  ${({ $highlighted, $hasIcon, theme }) =>
    cssInputBase($highlighted, $hasIcon, theme)}
  border: none;
  border-radius: ${remify(8)};

  &:invalid {
    ${({ $touched }) =>
      $touched && `box-shadow: inset 0 0 0 2px var(--inputValidation);`}
  }

  &:focus {
    outline: 1px solid var(--itemFocusFrame);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  ${fixFocusInSafari}
`;
